import api from 'services/api';
import { array, bool, enforce, nullable, numeric, object, oneOf, runSchema, str } from 'validation';

// GET /events/:id/sources — everything that can raise one event, behind a Live
// Events card's sources link. Backend:
// modules/generic_functions/events/getEventSources.js.
//
// Manual raise is deliberately not a source kind. Every event can be raised by
// hand, so listing it would make "no automatic source feeds this" — the
// diagnosis this modal exists for — invisible. The card's Raise button is that
// affordance.

export const SOURCE_KINDS = ['node_completion', 'flow_node_completion', 'runtime_limit', 'status_change_trigger', 'file_arrival'] as const;
export type EventSourceKind = (typeof SOURCE_KINDS)[number];

/** The event the sources belong to, for the modal's title. */
export interface SourcesEvent {
  readonly event_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly type: string;
  readonly zone_id: number;
  readonly zone_name: string;
}

/**
 * One source, flat across every kind with the inapplicable fields null.
 *
 * Not a discriminated union: the modal groups by `kind` and renders a
 * per-kind row, so it narrows by hand anyway, and `union()` would fail the
 * whole response on an unrecognised discriminator — the wrong trade for an
 * informational panel where one unknown source should not cost the operator
 * the other three.
 */
export interface EventSource {
  readonly kind: EventSourceKind;
  // node_completion + flow_node_completion + runtime_limit
  readonly node_definition_id: number | null;
  readonly node_name: string | null;
  /** For flow_node_completion this is the *flow's* zone, which can differ from the event's. */
  readonly zone_id: number | null;
  readonly zone_name: string | null;
  // node_completion + flow_node_completion
  readonly status: string | null;
  readonly precondition: string | null;
  readonly raiser_comment: string | null;
  // flow_node_completion — the placement the raise is configured on. `node_definition_id`
  // above names the child that completes; these name the flow and the step inside it, which
  // is where the configuration lives and where it has to be changed.
  readonly flow_definition_id: number | null;
  readonly flow_definition_name: string | null;
  readonly flow_node_id: string | null;
  readonly flow_node_label: string | null;
  /**
   * The child's own zone, which is not `zone_name` above — that is the flow's. The node
   * library is not zone-scoped, so a flow can place a child from another zone, and
   * same-named definitions across zones are common enough that #1030 exists.
   */
  readonly child_zone_name: string | null;
  // runtime_limit
  readonly runtime_limit_min: number | null;
  readonly runtime_limit_max: number | null;
  readonly runtime_limit_terminate: boolean | null;
  // status_change_trigger
  readonly trigger_id: number | null;
  readonly trigger_name: string | null;
  /** These can watch zones other than the event's own, which is otherwise invisible. */
  readonly monitor_all_zones: boolean | null;
  readonly from_status: string | null;
  readonly to_status: string | null;
  readonly watched_node_definition_id: number | null;
  readonly watched_node_name: string | null;
  // file_arrival
  /** False means the watcher is configured but switched off — a diagnosis, not an absence. */
  readonly enabled: boolean | null;
  readonly worker: string | null;
  readonly directory: string | null;
  readonly naming_pattern: string | null;
  readonly min_file_age: number | null;
  readonly poll_interval: number | null;
  readonly ssh_connection_string: string | null;
}

export interface EventSourcesResponse {
  readonly event: SourcesEvent;
  readonly sources: readonly EventSource[];
}

const eventSchema = object<SourcesEvent>({
  event_id: numeric(),
  name: str(),
  description: nullable(str()),
  type: str(),
  zone_id: numeric(),
  zone_name: str(),
});

// Every kind-inapplicable field is absent rather than null on the wire, so each
// is `nullable`, whose missing-key default of null is the value we want.
const sourceSchema = object<EventSource>({
  kind: oneOf(SOURCE_KINDS, 'node_completion'),
  node_definition_id: nullable(numeric()),
  node_name: nullable(str()),
  zone_id: nullable(numeric()),
  zone_name: nullable(str()),
  status: nullable(str()),
  precondition: nullable(str()),
  raiser_comment: nullable(str()),
  flow_definition_id: nullable(numeric()),
  flow_definition_name: nullable(str()),
  flow_node_id: nullable(str()),
  flow_node_label: nullable(str()),
  child_zone_name: nullable(str()),
  runtime_limit_min: nullable(numeric()),
  runtime_limit_max: nullable(numeric()),
  runtime_limit_terminate: nullable(bool()),
  trigger_id: nullable(numeric()),
  trigger_name: nullable(str()),
  monitor_all_zones: nullable(bool()),
  from_status: nullable(str()),
  to_status: nullable(str()),
  watched_node_definition_id: nullable(numeric()),
  watched_node_name: nullable(str()),
  enabled: nullable(bool()),
  worker: nullable(str()),
  directory: nullable(str()),
  naming_pattern: nullable(str()),
  min_file_age: nullable(numeric()),
  poll_interval: nullable(numeric()),
  ssh_connection_string: nullable(str()),
});

const responseSchema = object<EventSourcesResponse>({
  event: eventSchema,
  sources: array(sourceSchema),
});

/**
 * GET `/events/:id/sources`. Throws on transport error or structural
 * validation failure.
 *
 * `kind` is enforced per row: it selects which fields the modal reads, so a
 * drifted one would render a source under the wrong heading with every field
 * blank. Dropping that row keeps the remaining sources honest — and an empty
 * list is itself the answer to "why is this event starving?".
 */
export const getEventSources = async (eventId: number): Promise<EventSourcesResponse> => {
  const res = await api.get(`/events/${encodeURIComponent(eventId)}/sources`);
  const result = runSchema(responseSchema, res.data, 'getEventSources');
  if (!result.ok) {
    throw new Error('getEventSources: response failed validation');
  }
  return enforce(result.value, result.warnings, ['sources[*].kind', 'event.event_id']);
};
